import React, { useMemo, useCallback, useState } from "react";
import {
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Typography,
  Divider,
  Box,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { styled } from "@mui/material/styles";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { isNull } from "lodash";
import useStableCallback from "../../../hooks/useStableCallback";
import {
  multiplierOptions,
  multiplierMappings,
  singleValue,
  ENTITY_TYPES,
} from "../../../utils";

const ContentSx = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  height: "100%",
  p: 2,
};

const HeaderSx = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  mb: 2,
};

const DividerSx = { my: 2 };

const ButtonSx = { mr: 1 };

const FooterSx = {
  display: "flex",
  justifyContent: "flex-end",
};

const DatePickerProps = {
  sx: {
    color: (theme) => theme.palette.primary.main,
  },
};

const CustomDateTextField = styled(TextField)(() => ({
  ".MuiInputBase-input.Mui-disabled": {
    WebkitTextFillColor: "black",
  },
}));

const minValidDate = dayjs();

const maxValidDate = dayjs().add(6, "month");

const PONumberInputProps = {
  maxLength: 10,
};

const inputRegex = /^(?!.*[- ]{2})[A-Za-z0-9 ]*(?:[- &_][A-Za-z0-9 ]*){0,4}$/is;
const invalidRegex = /^[ -0]*$/;
const startEndSpecialCharRegex =
  /^[a-zA-Z0-9]+[a-zA-Z0-9&-_:.\- ]+[a-zA-Z0-9&-_:.\- ][a-zA-Z0-9]+$/;
const singleCharRegex =
  /^(?!.*[-]{2})(?=.*[a-zA-Z0-9]$)[a-zA-Z0-9][a-zA-Z0-9-]*$/;

const isValidPONumber = (value, setError) => {
  const currentValue = value.trim();
  if (!!currentValue) {
    if (
      currentValue.length <= 3 &&
      singleCharRegex.test(currentValue) &&
      !["0", "00", "000"].includes(currentValue)
    ) {
      setError({ error: false, helperText: "" });
      return true;
    }
    if (
      !currentValue.length ||
      !inputRegex.test(currentValue) ||
      invalidRegex.test(currentValue) ||
      !startEndSpecialCharRegex.test(currentValue)
    ) {
      setError({ error: true, helperText: "Invalid PO Number" });
      return false;
    }
    setError({ error: false, helperText: "" });
    return true;
  }
  setError({ error: true, helperText: "Please enter valid PO Number" });
  return false;
};

const selectionOptions = ["N", "Y", "E"];

const INIT_FORM_DATA = {
  selected: "",
  datingDays: "",
  coreDiscount: "",
  nonCoreDiscount: "",
  discountMultiplier: "",
  qtyMultiplier: "",
};

export const GeneratePOByMap = [
  {
    id: 1,
    label: "Department",
  },
  {
    id: 2,
    label: "POG",
  },
  {
    id: 3,
    label: "Custom",
  },
];

const SettingsForm = ({
  entityType = "",
  onSave = () => {},
  onCreatePONumbers = () => {},
  onSaveDates = () => {},
  onDeleteDates = () => {},
  onClose = () => {},
  isItemsDepartment = false,
}) => {
  const [formData, setFormData] = useState(INIT_FORM_DATA);
  const [generatePOBy, setGeneratePOBy] = useState(GeneratePOByMap[0].id);
  const [customPONumber, setCustomPONumber] = useState("");
  const [inputError, setInputError] = useState({
    error: false,
    helperText: "",
  });
  const [dateValue, setDateValue] = useState(null);

  const handleGeneratePOByChange = useStableCallback((event) => {
    setInputError({ error: false, helperText: "" });
    setCustomPONumber("");
    setGeneratePOBy(event?.target?.value);
  });

  const handleCustomPONumberInput = useStableCallback((event) => {
    const value = event?.target?.value ?? "";
    const updatedValue = value.toUpperCase();
    isValidPONumber(updatedValue, setInputError);
    setCustomPONumber(updatedValue);
  });

  const CreatePONumberDisabled =
    inputError.error ||
    (!customPONumber.trim().length && generatePOBy === GeneratePOByMap[2].id);

  const handleCreatePONumbers = useStableCallback(() => {
    onCreatePONumbers({
      generatePOBy: GeneratePOByMap[generatePOBy - 1].label,
      customPONumber: !!customPONumber ? customPONumber : null,
    });
  });

  const dateInputRenderer = useStableCallback((params) => (
    <CustomDateTextField
      {...params}
      disabled
      size="small"
      InputLabelProps={{ shrink: true }}
    />
  ));

  const handleDateChange = useStableCallback((newValue) => {
    setDateValue(newValue);
  });

  const handleSaveDates = useStableCallback(() => {
    const value = dateValue.startOf("date").format();
    const date = value.split("T")[0];
    onSaveDates(date);
  });

  const handleDeleteDates = useStableCallback(() => {
    onDeleteDates();
  });

  const handleFormData = useStableCallback((e) => {
    const fieldName = e.target.name;
    const newValue = String(e?.target?.value).trim();
    const discountFields = ["coreDiscount", "nonCoreDiscount", "datingDays"];
    if (
      [singleValue.no, singleValue.existing].includes(newValue) &&
      fieldName === "selected"
    ) {
      setFormData({ ...INIT_FORM_DATA, [fieldName]: newValue });
    } else {
      if (discountFields.includes(fieldName)) {
        const currentValue = !isNull(newValue) ? Number(newValue) : null;
        if (
          currentValue > (fieldName === discountFields[2] ? 365 : 100) ||
          currentValue < 0 ||
          isNaN(currentValue) ||
          (fieldName === discountFields[2] && String(newValue).includes("."))
        ) {
          return e.preventDefault();
        }
      }
      setFormData({ ...formData, [fieldName]: newValue });
    }
  });

  const handleSave = useCallback(() => {
    onSave({
      ...formData,
      datingDays: !!formData?.datingDays
        ? Number(formData?.datingDays)
        : formData?.datingDays,
    });
  }, [formData, onSave]);

  const handleReset = useStableCallback(() => {
    setFormData(INIT_FORM_DATA);
    setGeneratePOBy(GeneratePOByMap[0].id);
    setInputError({ error: false, helperText: "" });
    setCustomPONumber("");
    setDateValue(null);
  });

  const disableSaveDates = useMemo(() => {
    return (
      !dateValue ||
      !dayjs(dateValue).isValid() ||
      dateValue <= minValidDate.subtract(1, "day") ||
      dateValue > maxValidDate
    );
  }, [dateValue]);

  const freezeFields = useMemo(() => {
    return [singleValue.no, singleValue.existing].includes(formData.selected);
  }, [formData.selected]);

  const freezeDeptSaveAction = useMemo(() => {
    return (
      entityType === ENTITY_TYPES.department &&
      Object.keys(formData).every((key) =>
        key !== "selected" && !!formData[key]
          ? !String(formData[key]).trim()
          : true
      )
    );
  }, [entityType, formData]);

  return (
    <Box sx={ContentSx}>
      <Box>
        <Box sx={HeaderSx}>
          <Typography fontSize="1.1rem" fontWeight={600}>
            Settings
          </Typography>
          <IconButton aria-label="close" size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Grid container rowSpacing={2} columnSpacing={1}>
          {entityType !== ENTITY_TYPES.department && (
            <Grid item xs={6}>
              <FormControl fullWidth size="small" disabled={isItemsDepartment}>
                <InputLabel id="selected-dropdown">Selected</InputLabel>
                <Select
                  labelId="selected-dropdown"
                  label="Selected"
                  name="selected"
                  value={formData.selected}
                  onChange={handleFormData}
                >
                  <MenuItem dense value="">
                    <em>None</em>
                  </MenuItem>
                  {selectionOptions.map((option) => (
                    <MenuItem dense key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              label="Dating Days"
              name="datingDays"
              value={formData.datingDays}
              onChange={handleFormData}
              disabled={freezeFields}
              inputProps={{
                maxlength: 3,
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              label="Core Discount %"
              name="coreDiscount"
              value={formData.coreDiscount}
              onChange={handleFormData}
              disabled={freezeFields || isItemsDepartment}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              label="Non-Core Discount %"
              name="nonCoreDiscount"
              value={formData.nonCoreDiscount}
              onChange={handleFormData}
              disabled={freezeFields}
            />
          </Grid>
          <Grid item xs={6}>
            <FormControl
              fullWidth
              size="small"
              disabled={freezeFields || isItemsDepartment}
            >
              <InputLabel id="discount-multiplier-label">
                Discount Multiplier
              </InputLabel>
              <Select
                labelId="discount-multiplier-label"
                label="Discount Multiplier"
                name="discountMultiplier"
                value={formData.discountMultiplier}
                onChange={handleFormData}
              >
                <MenuItem dense value="">
                  <em>None</em>
                </MenuItem>
                {multiplierOptions.map((option) => (
                  <MenuItem dense key={option} value={option}>
                    {multiplierMappings[option]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl
              fullWidth
              size="small"
              disabled={freezeFields || isItemsDepartment}
            >
              <InputLabel id="qty-multiplier-label">Qty Multiplier</InputLabel>
              <Select
                labelId="qty-multiplier-label"
                label="Qty Multiplier"
                name="qtyMultiplier"
                value={formData.qtyMultiplier}
                onChange={handleFormData}
              >
                <MenuItem dense value="">
                  <em>None</em>
                </MenuItem>
                {multiplierOptions.map((option) => (
                  <MenuItem dense key={option} value={option}>
                    {multiplierMappings[option]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={freezeDeptSaveAction}
            >
              Save
            </Button>
          </Grid>
        </Grid>
        <Divider sx={DividerSx} />
        <Grid container rowSpacing={2} columnSpacing={1}>
          <Grid item xs={6}>
            <FormControl fullWidth size="small">
              <InputLabel id="generate-po-by">Generate PO By</InputLabel>
              <Select
                labelId="generate-po-by"
                label="Generate PO By"
                name="generatePOBy"
                value={generatePOBy}
                onChange={handleGeneratePOByChange}
              >
                {GeneratePOByMap.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {generatePOBy === GeneratePOByMap[2].id && (
            <Grid item xs={6}>
              <TextField
                required
                size="small"
                name="customPONumber"
                value={customPONumber}
                label="Custom PO Number"
                error={inputError.error}
                inputProps={PONumberInputProps}
                onChange={handleCustomPONumberInput}
                helperText={inputError.error && inputError.helperText}
                autoComplete="off"
              />
            </Grid>
          )}
          <Grid item xs={6}>
            <Button
              variant="contained"
              onClick={handleCreatePONumbers}
              disabled={CreatePONumberDisabled}
            >
              Create PO Numbers
            </Button>
          </Grid>
        </Grid>
        <Grid container spacing={1} mt={1}>
          <Grid item xs>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Release Date"
                renderInput={dateInputRenderer}
                OpenPickerButtonProps={DatePickerProps}
                minDate={minValidDate}
                maxDate={maxValidDate}
                value={dateValue}
                onChange={handleDateChange}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs="auto">
            <Button
              variant="contained"
              onClick={handleSaveDates}
              disabled={disableSaveDates}
              sx={ButtonSx}
            >
              Save Dates
            </Button>
            <Button variant="contained" onClick={handleDeleteDates}>
              Delete Dates
            </Button>
          </Grid>
        </Grid>
      </Box>
      <Box sx={FooterSx}>
        <Button variant="outlined" onClick={onClose} sx={ButtonSx}>
          Cancel
        </Button>
        <Button variant="outlined" onClick={handleReset}>
          Reset
        </Button>
      </Box>
    </Box>
  );
};
export default SettingsForm;
